'use client'

import { useEffect, useId, useRef } from 'react'
import { CircleHelp, Info, X } from 'lucide-react'
import { useFiltersStore } from '@/lib/store/filters-store'
import SortFilter from './sort-filter'
import styles from './category-tree-controls.module.scss'
import type { CategoryEditorialFields } from '@/lib/categories/category-editorial-fields'
import { startAssetsGridTransition } from '@/lib/hooks/use-assets-grid-transition'

function CategorySummaryControls({ summary1, summary2 }: Pick<CategoryEditorialFields, 'summary1' | 'summary2'>) {
  const groupName = useId()
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!summary1 && !summary2) return
    const close = (event: PointerEvent | KeyboardEvent) => {
      const opened = root.current?.querySelector<HTMLDetailsElement>('details[open]')
      if (!opened) return
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Escape') return
        opened.open = false
        opened.querySelector('summary')?.focus()
      } else if (event.target instanceof Node && !root.current?.contains(event.target)) {
        opened.open = false
      }
    }
    document.addEventListener('pointerdown', close)
    document.addEventListener('keydown', close)
    return () => {
      document.removeEventListener('pointerdown', close)
      document.removeEventListener('keydown', close)
    }
  }, [summary1, summary2])

  if (!summary1 && !summary2) return null

  return (
    <div className={styles.summaryControls} ref={root}>
      {[
        { html: summary1, label: 'Category information', Icon: Info },
        { html: summary2, label: 'Category help', Icon: CircleHelp },
      ].map(({ html, label, Icon }, index) =>
        html ? (
          <details key={index} name={groupName} className={styles.summaryItem}>
            <summary className={styles.iconButton} aria-label={label} title={label}>
              <Icon size={16} aria-hidden="true" />
            </summary>
            <section className={styles.summaryCard} aria-label={label}>
              <div className={styles.summaryHeading}>
                <span>{label}</span>
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label={`Close ${label.toLowerCase()}`}
                  onClick={(event) => {
                    const details = event.currentTarget.closest('details')
                    if (details) {
                      details.open = false
                      details.querySelector('summary')?.focus()
                    }
                  }}
                >
                  <X size={14} />
                </button>
              </div>
              <div className={styles.summaryHtml} dangerouslySetInnerHTML={{ __html: html }} />
            </section>
          </details>
        ) : null
      )}
    </div>
  )
}

export function CategoryTreeFooter({ fields }: { mainCategorySlug?: string | null; fields?: CategoryEditorialFields }) {
  return (
    <div className={styles.controls}>
      <CategorySummaryControls summary1={fields?.summary1} summary2={fields?.summary2} />
    </div>
  )
}

export function CategoryTreeSort() {
  const sort = useFiltersStore((state) => state.filters.find((filter) => filter.__typename === 'SortFilter'))
  const setFilterValue = useFiltersStore((state) => state.setFilterValue)

  return (
    <SortFilter
      iconOnly
      value={sort?.value}
      enabled={!!sort?.enabled && sort.visible}
      onChange={(value) => setFilterValue('sort', value)}
      triggerClassName={styles.sortTrigger}
      contentClassName="tw-min-w-40"
    />
  )
}

export function CategoryTreeFreeToggle() {
  const premium = useFiltersStore((state) => state.filters.find((filter) => filter.__typename === 'PremiumFilter'))
  const setFilterValue = useFiltersStore((state) => state.setFilterValue)
  const active = premium?.value === false

  return (
    <button
      type="button"
      className={styles.freeToggle}
      aria-pressed={active}
      disabled={!premium?.enabled}
      onClick={() => {
        if (
          document.querySelector(
            '[data-slot="assets-grid"], [data-slot="initial-assets-grid"], [data-slot="search-results"]'
          )
        ) {
          startAssetsGridTransition()
        }
        setFilterValue('premium', active ? undefined : false)
      }}
    >
      <span className={styles.freeIndicator} aria-hidden="true" />
      <span>Show Free Only</span>
    </button>
  )
}
